import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchSyncRunExport,
  type SyncRunRow,
  useSyncRuns,
  useTenants,
} from '../hooks/api';
import { Loading, ErrorBox, EmptyBox } from '../components/Loading';
import { Status, statusKindForJobStatus } from '../components/Status';
import { ApiError, formatNumber, formatTimestamp } from '../api';

const STATUS_FILTERS = ['', 'queued', 'running', 'succeeded', 'failed', 'skipped'];
const ENVIRONMENT_FILTERS = ['', 'live', 'sandbox'];

export default function SyncLogsPage() {
  const [searchParams] = useSearchParams();
  const tenants = useTenants();
  const tenantCodes = useMemo(() => tenants.data?.data.map((t) => t.code) ?? [], [tenants.data]);

  const [tenantFilter, setTenantFilter] = useState(() => searchParams.get('tenant') ?? '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [environmentFilter, setEnvironmentFilter] = useState(
    () => searchParams.get('environment') ?? '',
  );
  const [sourceSystemFilter, setSourceSystemFilter] = useState(
    () => searchParams.get('source_system') ?? '',
  );
  const [entityFilter, setEntityFilter] = useState(() => searchParams.get('entity') ?? '');

  const visibleTenants = tenantFilter ? [tenantFilter] : tenantCodes;

  return (
    <div className="space-y-3">
      <h1 className="text-[14px] font-semibold uppercase tracking-wide text-neutral-700 border-b border-neutral-300 pb-1">
        Sync-Logs (Cross-Tenant)
      </h1>

      <div className="panel">
        <div className="panel-h">Filter</div>
        <div className="panel-b grid grid-cols-5 gap-3">
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Tenant</label>
            <select
              className="input w-full"
              value={tenantFilter}
              onChange={(e) => setTenantFilter(e.target.value)}
            >
              <option value="">-- alle --</option>
              {tenantCodes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Status</label>
            <select
              className="input w-full"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              {STATUS_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s || '-- alle --'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Umgebung</label>
            <select
              className="input w-full"
              value={environmentFilter}
              onChange={(e) => setEnvironmentFilter(e.target.value)}
            >
              {ENVIRONMENT_FILTERS.map((s) => (
                <option key={s} value={s}>
                  {s || '-- alle --'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">System</label>
            <input
              type="text"
              className="input w-full"
              placeholder="weclapp"
              value={sourceSystemFilter}
              onChange={(e) => setSourceSystemFilter(e.target.value.trim())}
            />
          </div>
          <div>
            <label className="block text-[11px] text-neutral-600 mb-0.5">Tabelle</label>
            <input
              type="text"
              className="input w-full"
              placeholder="sales_orders"
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value.trim())}
            />
          </div>
        </div>
      </div>

      {tenants.isLoading && <Loading />}
      {tenants.error && <ErrorBox error={tenants.error} />}

      {visibleTenants.map((code) => (
        <TenantSyncRunsBlock
          key={code}
          tenantCode={code}
          filter={{
            ...(statusFilter ? { status: statusFilter } : {}),
            ...(environmentFilter ? { environment: environmentFilter } : {}),
            ...(sourceSystemFilter ? { source_system: sourceSystemFilter } : {}),
            ...(entityFilter ? { entity: entityFilter } : {}),
            limit: 200,
            offset: 0,
          }}
        />
      ))}

      {visibleTenants.length === 0 && <EmptyBox>Keine Tenants ausgewaehlt.</EmptyBox>}
    </div>
  );
}

function TenantSyncRunsBlock({
  tenantCode,
  filter,
}: {
  tenantCode: string;
  filter: {
    status?: string;
    source_system?: string;
    entity?: string;
    environment?: string;
    limit: number;
    offset: number;
  };
}) {
  const runs = useSyncRuns(tenantCode, filter);
  const lastUpdated =
    runs.dataUpdatedAt > 0 ? formatTimestamp(new Date(runs.dataUpdatedAt).toISOString()) : '-';

  return (
    <div className="panel">
      <div className="panel-h flex items-center justify-between">
        <span>{tenantCode}</span>
        <div className="ml-auto mr-2 flex items-center gap-2 text-[10px] text-neutral-500">
          <Status kind={runs.isFetching ? 'warn' : 'ok'}>
            {runs.isFetching ? 'sync' : 'live'}
          </Status>
          <span className="font-mono">{lastUpdated}</span>
        </div>
        <span className="text-[10px] text-neutral-500 font-mono">
          {runs.data ? `${runs.data.data.length}/${runs.data.total}` : '--'}
        </span>
      </div>
      <div className="panel-b p-0">
        {runs.isLoading && (
          <div className="p-2">
            <Loading />
          </div>
        )}
        {runs.error && (
          <div className="p-2">
            <ErrorBox error={runs.error} />
          </div>
        )}
        {runs.data && runs.data.data.length === 0 && (
          <div className="p-2">
            <EmptyBox>Keine Sync-Laeufe fuer diesen Filter.</EmptyBox>
          </div>
        )}
        {runs.data && runs.data.data.length > 0 && (
          <div className="overflow-hidden">
            <table className="tbl table-fixed text-[11px]">
              <colgroup>
                <col className="w-[17%]" />
                <col className="w-[20%]" />
                <col className="w-[14%]" />
                <col className="w-[9%]" />
                <col className="w-[20%]" />
                <col className="w-[14%]" />
                <col className="w-[6%]" />
              </colgroup>
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Ziel</th>
                  <th>Typ</th>
                  <th>Status</th>
                  <th>Zeilen</th>
                  <th>Trace / Fehler</th>
                  <th>Export</th>
                </tr>
              </thead>
              <tbody>
                {runs.data.data.map((r) => (
                  <SyncRunRowView key={r.id} tenantCode={tenantCode} row={r} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function SyncRunRowView({ tenantCode, row }: { tenantCode: string; row: SyncRunRow }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  async function exportTxt() {
    setExporting(true);
    setExportError(null);
    try {
      const text = await fetchSyncRunExport(tenantCode, row.id);
      downloadText(
        text,
        `sync-run-${safeFilePart(tenantCode)}-${safeFilePart(row.source_system)}-${safeFilePart(row.entity)}-${row.id}.txt`,
      );
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setExporting(false);
    }
  }

  return (
    <tr>
      <td className="mono leading-tight">
        <div title={`Start: ${formatTimestamp(row.started_at)}`}>
          Start {formatCompactTimestamp(row.started_at)}
        </div>
        <div title={`Ende: ${formatTimestamp(row.finished_at)}`}>
          Ende {formatCompactTimestamp(row.finished_at)}
        </div>
        <div className="text-neutral-600">{formatDuration(row.duration_ms)}</div>
      </td>
      <td className="leading-tight">
        <div className="flex items-center gap-1">
          <span className="mono truncate">{row.source_system}</span>
          <Status kind={row.environment === 'live' ? 'ok' : 'info'}>{row.environment}</Status>
        </div>
        <div className="mono truncate" title={row.entity}>
          {row.entity}
        </div>
      </td>
      <td className="mono leading-tight">
        <div className="truncate" title={row.strategy}>
          {row.strategy}
        </div>
        <div className="truncate text-neutral-600" title={row.trigger_type}>
          {row.trigger_type}
        </div>
      </td>
      <td>
        <Status kind={statusKindForJobStatus(row.status)}>{row.status}</Status>
      </td>
      <td className="mono leading-tight" title={rowCountTitle(row)}>
        <div>
          Verarbeitet {formatRunNumber(row.rows_processed)} · Neu{' '}
          {formatRunNumber(row.rows_created)}
        </div>
        <div>
          Aktualisiert {formatRunNumber(row.rows_updated)} · Uebersprungen{' '}
          {formatRunNumber(row.rows_skipped)} · Anfragen {formatNumber(row.request_count)}
        </div>
      </td>
      <td className="leading-tight">
        <div className="mono truncate" title={row.trace_id ?? ''}>
          {row.trace_id ?? ''}
        </div>
        <div className="text-st-err truncate" title={exportError ?? row.error_message ?? ''}>
          {exportError ?? row.error_message ?? ''}
        </div>
      </td>
      <td>
        <button type="button" className="btn w-full px-1 text-[11px]" disabled={exporting} onClick={exportTxt}>
          {exporting ? '...' : 'TXT'}
        </button>
      </td>
    </tr>
  );
}

function formatRunNumber(value: number | string): string {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? formatNumber(n) : String(value);
}

function formatDuration(value: number | string | null): string {
  if (value === null || value === undefined) return '--';
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  if (n < 1000) return `${Math.round(n)} ms`;
  if (n < 60_000) return `${(n / 1000).toFixed(1)} s`;
  return `${Math.round(n / 60_000)} min`;
}

function formatCompactTimestamp(value: string | null): string {
  if (!value) return '--';
  const formatted = formatTimestamp(value);
  const compact = formatted.replace(/^2026-/, '').replace(' ', ' ');
  return compact.length > 16 ? compact.slice(0, 16) : compact;
}

function rowCountTitle(row: SyncRunRow): string {
  return [
    `Verarbeitet: ${formatRunNumber(row.rows_processed)}`,
    `Neu: ${formatRunNumber(row.rows_created)}`,
    `Aktualisiert: ${formatRunNumber(row.rows_updated)}`,
    `Uebersprungen: ${formatRunNumber(row.rows_skipped)}`,
    `Anfragen: ${formatNumber(row.request_count)}`,
  ].join('\n');
}

function downloadText(text: string, filename: string): void {
  const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function safeFilePart(value: string): string {
  return value.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/^-+|-+$/g, '') || 'sync';
}
